import React, {
  memo,
  PureComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AppState, Platform, View, Linking } from "react-native";
import codePush from "react-native-code-push";
import messaging from "@react-native-firebase/messaging";
import AppNavigation from "./navigation";
import { batch, connect, useDispatch, useSelector } from "react-redux";
import { OfflineNotice } from "./components/common";
import MessageAlert from "./navigation/MessageAlert";
import { fetchConfig } from "@microdoing/shared/services/firebase";
import {
  isIos,
  isWeb,
  isWebMobile,
} from "@microdoing/shared/utils/deviceDetectors";
import socket from "@microdoing/shared/services/socket";
import dynamicLinks from "@react-native-firebase/dynamic-links";
import { changeSignupInput } from "@microdoing/shared/services/signup/actions";
import { loginRequest } from "@microdoing/shared/services/login/actions";
import { changeAppState } from "@microdoing/shared/services/nav/actions";
import {
  getHasCompleteOnboarding,
  getUserId,
} from "@microdoing/shared/services/user/selectors";
import { getCurrentUserCampaignRequest } from "@microdoing/shared/services/userCampaign/actions";
import { getActiveCoachingSessionRequest } from "@microdoing/shared/services/coaching/actions";
import { getActiveSurveyRequest } from "@microdoing/shared/services/survey/actions";
import { updateUserWithoutResponseRequest } from "@microdoing/shared/services/user/actions";
import moment from "moment";
import hasNotificationPermission from "./utils/hasNotificationPermission";
import { TOKEN_REFRESH } from "@microdoing/shared/services/token/constants";
import { getRefreshTokenExpiresAt } from "@microdoing/shared/services/token/selector";
import PushNotification from "react-native-push-notification";
import CodepushLoading from "./CodepushLoading";
import SplashScreen from "react-native-splash-screen";
import getUrlParams from "./utils/getUrlParams";
import { store } from "@microdoing/shared/services/store";

const SafeAreaApp = () => {
  const [isCodePushLoading, setCodePushLoading] = useState(false);
  const [codePushStatus, setCodePushStatus] = useState("");
  const [codePushProgress, setCodePushProgress] = useState(0);

  const dispatch = useDispatch();

  const appState = useRef(AppState.currentState);
  const hasCompleteOnboarding = useSelector(getHasCompleteOnboarding);
  const userId = useSelector(getUserId);

  const refreshTokenExpiresAt = useSelector(getRefreshTokenExpiresAt);

  const handleCodePush = useCallback(async () => {
    try {
      console.log("code push");
      const update = await codePush.checkForUpdate();
      console.log("update", update);
      if (update) {
        setCodePushLoading(true);
        const { IMMEDIATE } = codePush.InstallMode;
        // React to the sync status changes
        const promise = await codePush.sync(
          { installMode: IMMEDIATE },
          status => {
            setCodePushStatus(status);
          },
          ({ receivedBytes, totalBytes }) => {
            /* Update download modal progress */
            setCodePushProgress((receivedBytes / totalBytes) * 100);
          },
        );
      }
    } catch (e) {
      console.log("CODE PUSH ERRO");
      console.log(e);
      // codePush.notifyAppReady();
      setCodePushLoading(false);
    }
  }, []);

  useEffect(() => {
    handleCodePush();
  }, []);

  const handleDynamicLink = useCallback(async link => {
    const url = link?.url;
    const params = getUrlParams(url);
    const { route = "Home" } = params;
    if (params.code) {
      dispatch(changeSignupInput({ accessCode: params?.code?.toUpperCase() }));
    }
    if (params.authToken) {
      dispatch(loginRequest({ authToken: params?.authToken, route }));
    }
  }, []);

  const unsubscribeDynamicLinks = useCallback(() => {
    if (isWeb) return () => null;
    return dynamicLinks().onLink(handleDynamicLink);
  }, []);

  useEffect(() => {
    if (refreshTokenExpiresAt) {
      const diff = moment(refreshTokenExpiresAt).diff(moment(), "days");
      if (diff <= 2) {
        dispatch({ type: TOKEN_REFRESH });
      }
    }
  }, [refreshTokenExpiresAt]);

  useEffect(() => {
    socket.on("request-init", async () => {
      const storeState = await store.getState();
      const userId = getUserId(storeState);
      socket.emit("init", userId);
    });

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("pong");
    };
  }, []);

  useEffect(() => {
    const appStateSubscription = AppState.addEventListener(
      "change",
      async nextAppState => {
        if (nextAppState.match(/active/)) {
          const storeState = await store.getState();
          const userId = getUserId(storeState);
          const hasCompleteOnboarding = getHasCompleteOnboarding(storeState);
          console.log("hello");

          handleCodePush();

          if (!socket.connected) {
            socket.connect();
          }
          // check if refresh token

          if (hasCompleteOnboarding && userId) {
            batch(() => {
              dispatch(getCurrentUserCampaignRequest({ userId }));
              dispatch(getActiveCoachingSessionRequest({ userId }));
              dispatch(getActiveSurveyRequest({ userId }));
            });
            PushNotification.setApplicationIconBadgeNumber(0);

            // check notifications
            hasNotificationPermission().then(enabled => {
              dispatch(
                updateUserWithoutResponseRequest({
                  userId,
                  hasNotificationsEnabled: enabled,
                  hasNotificationsEnabledCheckDate: moment().toDate(),
                }),
              );
            });
          }
        } else if (nextAppState !== "active") {
          socket.emit("logout", userId);
        }

        dispatch(changeAppState(nextAppState));
      },
    );

    return () => {
      appStateSubscription.remove();
    };
  }, []);

  useEffect(() => {
    /**
     * Web remove loader
     */

    if (isWeb) {
      // Initial loader
      const loader = document?.querySelector(".preloader");

      loader?.classList?.remove("preloader");
      loader?.classList?.add("loader-hide");
    }

    /**
     * Hide SplashScreen
     */

    if (!isWeb) {
      SplashScreen.hide();
    }

    /**
     * Socket disconnect Logic
     */

    socket.on("disconnect", reason => {});

    /**
     * Dynamic Links
     */

    let dynamicLinksListener: any = () => null;
    let messageListener = () => null;

    if (!isWeb) {
      fetchConfig();
      dynamicLinks()
        .getInitialLink()
        .then(link => {
          if (link?.url) {
            handleDynamicLink(link);
          }
        });

      if (isIos) {
        dynamicLinksListener = Linking.addEventListener("url", async link => {
          const resolved = await dynamicLinks().resolveLink(link.url);
          handleDynamicLink(resolved);
        });
      } else {
        dynamicLinksListener = dynamicLinks().onLink(handleDynamicLink);
      }

      const messageListener = messaging().onMessage(async remoteMessage => {});

      messaging()
        .getInitialNotification()
        .then((notificationOpen: NotificationOpen) => {
          if (notificationOpen) {
            // App was opened by a notification
            // Get the action triggered by the notification being opened
            const action = notificationOpen.action;
            // Get information about the notification that was opened
            const notification: Notification = notificationOpen.notification;
            if (notification?.data?.type === "message") {
              // navigate to message
              // navigatorHelper.navigate("Message");
            }
          }
        });

      messaging().onNotificationOpenedApp(remoteMessage => {
        // this.onNotif(remoteMessage?.notification);
        const notification = remoteMessage.notification;
        if (notification?.data?.type === "message") {
          // navigate to message
          // navigatorHelper.navigate("Message");
        }
      });
    }

    /**
     * Messaging
     */

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("pong");
      unsubscribeDynamicLinks();
      messageListener();
    };
  }, []);

  return (
    <View
      style={
        Platform.OS !== "web"
          ? { flex: 1 }
          : {
              width: isWebMobile ? "100vw" : "100%",
              height: isWebMobile ? "100vh" : "auto",
              maxWidth: isWebMobile ? "100vw" : "100%",
              maxHeight: isWebMobile ? "100vh" : "auto",
              overflow: isWebMobile ? "hidden" : "visible",
              position: "relative",
              display: "flex",
              flex: 1,
            }
      }
    >
      <CodepushLoading
        codePushProgress={codePushProgress}
        codePushStatus={codePushStatus}
        isLoading={isCodePushLoading}
        // isLoading
      />
      <MessageAlert />

      <OfflineNotice />
      <AppNavigation />
    </View>
  );
};

export default memo(SafeAreaApp);
